import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	CashdeskTransaction,
	createWithdrawPlayerMutation,
	Player,
	WithdrawPlayerMutationParams,
} from 'shared/api';
import {
	greaterZeroRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type WithdrawPlayerFormValue = {
	amount: string;
};

export type WithdrawPlayerModel = ModalFormModel<Player, WithdrawPlayerFormValue> & {
	withdrawPlayerMutation: Mutation<
		WithdrawPlayerMutationParams,
		CashdeskTransaction,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createWithdrawPlayerModel = (): WithdrawPlayerModel => {
	const withdrawPlayerMutation = createWithdrawPlayerMutation();

	const model = createModalFormModel<Player, WithdrawPlayerFormValue>({
		fields: {
			amount: {
				init: '0',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = withdrawPlayerMutation;

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			player: model.$modalData,
		},
		filter: ({ player }) => player !== null,
		fn: ({ player, values }) => ({
			id: player!.id,
			amount: Number(values.amount),
		}),
		target: withdrawPlayerMutation.start,
	});

	chain(withdrawPlayerMutation.finished.success, model.switcher.disable);

	notify(withdrawPlayerMutation.finished.success, 'withdraw-player.success');

	chain(model.modalClosed, withdrawPlayerMutation.reset);

	return {
		withdrawPlayerMutation,
		$pending,
		...model,
	};
};
